import { useMemo } from "react";
import {
  Scale,
  TrendingDown,
  User,
  UserPlus,
  Users,
} from "lucide-react";
import {
  buildAgeGenderActions,
  buildAgeGenderHero,
  buildAgeGenderKpis,
  buildAgeTableSampleRows,
  buildComposedAgeGenderChart,
  buildGenderSplitMetrics,
  buildGpiGaugeStyle,
  buildGpiLineChart,
  buildJourneyCards,
  buildStageGenderCards,
  fmt,
} from "@/utils/ageEnrolmentDerived";
import "./AgeEnrolmentAgeGenderTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const KPI_ICONS = {
  "": Users,
  pink: User,
  green: Scale,
  orange: TrendingDown,
};

const ACTION_ICONS = {
  pink: UserPlus,
  green: Scale,
  orange: TrendingDown,
};

const AgeEnrolmentAgeGenderTab = ({ overview = {}, ageData = [], blockData = [] }) => {
  const gpi = overview.gender_parity_index ?? 0;

  const gaugeStyle = useMemo(() => buildGpiGaugeStyle(gpi), [gpi]);
  const hero = useMemo(() => buildAgeGenderHero(overview), [overview]);
  const split = useMemo(() => buildGenderSplitMetrics(overview), [overview]);
  const kpis = useMemo(() => buildAgeGenderKpis(overview), [overview]);
  const composed = useMemo(() => buildComposedAgeGenderChart(ageData), [ageData]);
  const stages = useMemo(() => buildStageGenderCards(ageData), [ageData]);
  const gpiLine = useMemo(() => buildGpiLineChart(ageData), [ageData]);
  const tableRows = useMemo(() => buildAgeTableSampleRows(ageData), [ageData]);
  const journey = useMemo(() => buildJourneyCards(overview, ageData), [overview, ageData]);
  const actions = useMemo(
    () => buildAgeGenderActions(overview, ageData, blockData),
    [overview, ageData, blockData]
  );

  return (
    <div className="aag-tab" data-testid="age-enrolment-age-gender">
      <section className="aag-hero-card" id="aag-hero">
        <div className="aag-gender-hero-layout">
          <div>
            <div className="aag-gpi-main">
              <div
                className="aag-gauge"
                style={gaugeStyle}
                aria-label={`Gender Parity Index ${hero.gpiLabel}`}
              >
                <div>
                  <strong>{hero.gpiLabel}</strong>
                  <span>GPI</span>
                </div>
              </div>
              <div className="aag-hero-copy">
                <h2>{toTitleCase(hero.headline)}</h2>
                <p>{hero.narrative}</p>
                <div className="aag-status-row">
                  {hero.chips.map((chip) => (
                    <span key={chip.label} className={`aag-status-chip ${chip.className}`.trim()}>
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="aag-summary-note">
              <div className="aag-icon-box pink">
                <UserPlus size={22} strokeWidth={2} />
              </div>
              <div>
                <b>Primary district action</b>
                <span>{hero.action}</span>
              </div>
            </div>
          </div>

          <div className="aag-gender-split-card" aria-label="Gender split summary">
            <div className="aag-split-head">
              <div>
                <h3>Gender Split Across Total Enrolment</h3>
                <p>
                  Boys and girls remain broadly balanced, but a {fmt(split.gap)} learner gap needs
                  stage-level action.
                </p>
              </div>
              <span className="aag-pill">{split.totalLabel} students</span>
            </div>
            <div className="aag-split-bar" aria-hidden="true">
              <div className="boys" style={{ width: `${split.boysWidth}%` }} />
              <div className="girls" style={{ width: `${split.girlsWidth}%` }} />
            </div>
            <div className="aag-split-metrics">
              <div className="aag-split-mini">
                <small>Boys</small>
                <strong className="aag-boys-text">{fmt(split.boys)}</strong>
                <span>{split.boysPct.toFixed(1)}% share</span>
              </div>
              <div className="aag-split-mini">
                <small>Girls</small>
                <strong className="aag-girls-text">{fmt(split.girls)}</strong>
                <span>{split.girlsPct.toFixed(1)}% share</span>
              </div>
              <div className="aag-split-mini">
                <small>Gap</small>
                <strong>{fmt(split.gap)}</strong>
                <span>Focus on secondary age groups</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="aag-kpi-grid" aria-label="Age and gender KPIs">
        {kpis.map((kpi) => {
          const Icon = KPI_ICONS[kpi.iconClass] || Users;
          return (
            <article key={kpi.label} className="aag-kpi-card">
              <div className="aag-kpi-head">
                <div className={`aag-kpi-icon ${kpi.iconClass}`.trim()}>
                  <Icon size={22} strokeWidth={2} />
                </div>
                <span className="aag-pill">{kpi.pill}</span>
              </div>
              <small>{kpi.label}</small>
              <strong>{kpi.value}</strong>
              <span>{kpi.sub}</span>
            </article>
          );
        })}
      </section>

      <div className="aag-section-title" id="aag-diagnostics">
        <div>
          <h2>Boys vs Girls by Age</h2>
          <p>
            The main comparison chart is given a full-width treatment and cleaner legends, so users
            can read the age pattern without the previous compressed layout.
          </p>
        </div>
        <button type="button" className="aag-section-action" onClick={() => scrollToId("aag-table")}>
          Open age table →
        </button>
      </div>

      <section className="aag-analytics-grid">
        <article className="aag-panel aag-chart-card">
          <div className="aag-panel-head">
            <div>
              <h3>Age-Wise Enrolment and GPI Trend</h3>
              <p>
                Bars show boys and girls. The line shows gender parity movement against the right
                axis.
              </p>
            </div>
            <span className="aag-pill">Ages 4–18</span>
          </div>
          <div className="aag-chart-shell">
            <svg viewBox="0 0 980 390" role="img" aria-label="Boys girls and GPI by age chart">
              <defs>
                <linearGradient id="aagBoysGrad" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" stopColor="#60a5fa" />
                  <stop offset="1" stopColor="#2563eb" />
                </linearGradient>
                <linearGradient id="aagGirlsGrad" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" stopColor="#f472b6" />
                  <stop offset="1" stopColor="#ec4899" />
                </linearGradient>
                <filter id="aagSoftShadow" x="-10%" y="-10%" width="120%" height="120%">
                  <feDropShadow dx="0" dy="8" stdDeviation="10" floodColor="#1f4a8b" floodOpacity="0.08" />
                </filter>
              </defs>
              <g transform="translate(58,28)">
                <g stroke="#dbe7f7" strokeWidth="1" strokeDasharray="4 4">
                  {composed.yTicks.map((tick) => (
                    <line key={`h-${tick.y}`} x1="0" y1={tick.y} x2="860" y2={tick.y} />
                  ))}
                  {composed.bars.map((bar) => (
                    <line key={`v-${bar.age}`} x1={bar.boys.x} y1="0" x2={bar.boys.x} y2="288" />
                  ))}
                </g>
                <g className="aag-axis-label" textAnchor="end">
                  {composed.yTicks.map((tick) => (
                    <text key={`yl-${tick.label}`} x="-12" y={tick.y + 4}>
                      {tick.label}
                    </text>
                  ))}
                </g>
                <g className="aag-axis-label" textAnchor="start">
                  {composed.gpiTicks.map((tick) => (
                    <text key={`gr-${tick.label}`} x="872" y={tick.y + 4}>
                      {tick.label}
                    </text>
                  ))}
                </g>
                <g filter="url(#aagSoftShadow)">
                  {composed.bars.map((bar) => (
                    <g key={`bars-${bar.age}`}>
                      <rect
                        x={bar.boys.x}
                        y={bar.boys.y}
                        width="22"
                        height={Math.max(bar.boys.h, 0)}
                        rx="4"
                        fill="url(#aagBoysGrad)"
                      />
                      <rect
                        x={bar.girls.x}
                        y={bar.girls.y}
                        width="22"
                        height={Math.max(bar.girls.h, 0)}
                        rx="4"
                        fill="url(#aagGirlsGrad)"
                      />
                    </g>
                  ))}
                </g>
                <polyline
                  points={composed.gpiPoints}
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <g fill="#10b981" stroke="#fff" strokeWidth="2">
                  {composed.bars.map((bar) => (
                    <circle key={`gp-${bar.age}`} cx={bar.gpi.x} cy={bar.gpi.y} r="5" />
                  ))}
                </g>
                <g className="aag-axis-label" textAnchor="middle">
                  {composed.bars.map((bar) => (
                    <text key={`ax-${bar.age}`} x={bar.boys.x + 11} y="318">
                      {bar.age}
                    </text>
                  ))}
                </g>
                <rect
                  x={composed.zoneStart}
                  y="0"
                  width={composed.zoneWidth}
                  height="288"
                  rx="16"
                  fill="#ef4444"
                  opacity="0.035"
                />
                <text x={composed.zoneStart + composed.zoneWidth / 2} y="22" textAnchor="middle" fill="#b91c1c" fontSize="12" fontWeight="900">
                  secondary retention zone
                </text>
              </g>
            </svg>
          </div>
          <div className="aag-legend-wrap">
            <span>
              <i className="aag-legend-dot" />
              Boys enrolment
            </span>
            <span>
              <i className="aag-legend-dot girls" />
              Girls enrolment
            </span>
            <span>
              <i className="aag-legend-dot gpi" />
              Gender Parity Index
            </span>
          </div>
        </article>

        <aside className="aag-panel">
          <div className="aag-panel-head">
            <div>
              <h3>Stage-Wise Gender Balance</h3>
              <p>Use lifecycle grouping to move from chart observation to intervention planning.</p>
            </div>
            <span className="aag-pill">Stage view</span>
          </div>
          <div className="aag-stage-stack">
            {stages.map((stage) => (
              <div key={stage.title} className="aag-stage-card">
                <div className="aag-stage-top">
                  <b>{stage.title}</b>
                  <span>{stage.total}</span>
                </div>
                <div className="aag-stage-bars">
                  <div className="aag-mini-track">
                    <i style={{ width: `${stage.boysWidth}%` }} />
                  </div>
                  <div className="aag-mini-track pink">
                    <i style={{ width: `${stage.girlsWidth}%` }} />
                  </div>
                </div>
                <div className="aag-stage-meta">
                  <span>{stage.metaLeft}</span>
                  <span>{stage.metaRight}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="aag-risk-card">
            <b>What needs attention?</b>
            <span>
              Secondary ages show declining enrolment and girls participation. Prioritize block-level
              attendance, transition, transport, safety and re-enrolment follow-up for ages 14–18.
            </span>
          </div>
        </aside>
      </section>

      <div className="aag-section-title" id="aag-table">
        <div>
          <h2>GPI Diagnostics and Age Table</h2>
          <p>
            The detailed table is separated from the main chart so users can scan the pattern first
            and then investigate exact values.
          </p>
        </div>
        <button type="button" className="aag-section-action" onClick={() => scrollToId("aag-actions")}>
          View priority actions →
        </button>
      </div>

      <section className="aag-two-column">
        <article className="aag-panel">
          <div className="aag-panel-head">
            <div>
              <h3>Gender Parity Index by Age</h3>
              <p>
                GPI remains near parity through primary and upper-primary ages, then drops sharply at
                the end of the age range.
              </p>
            </div>
            <span className="aag-pill">1.0 = parity</span>
          </div>
          <div className="aag-line-chart-wrap">
            <svg viewBox="0 0 610 330" role="img" aria-label="Gender parity index line chart">
              <g transform="translate(48,30)">
                <g stroke="#dbe7f7" strokeDasharray="4 4">
                  {gpiLine.yTicks.map((tick) => (
                    <line key={`gl-${tick.label}`} x1="0" y1={tick.y} x2={gpiLine.chartW} y2={tick.y} />
                  ))}
                </g>
                <g className="aag-axis-label" textAnchor="end">
                  {gpiLine.yTicks.map((tick) => (
                    <text key={`gyl-${tick.label}`} x="-10" y={tick.y + 4}>
                      {tick.label}
                    </text>
                  ))}
                </g>
                <path
                  d={gpiLine.linePath}
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="4"
                  strokeLinecap="round"
                />
                <g fill="#10b981" stroke="#fff" strokeWidth="2">
                  {gpiLine.points.map((p) => (
                    <circle key={`gpc-${p.age}`} cx={p.x} cy={p.y} r="5" />
                  ))}
                </g>
                <line
                  x1="0"
                  y1={gpiLine.parityY}
                  x2={gpiLine.chartW}
                  y2={gpiLine.parityY}
                  stroke="#14b8a6"
                  strokeDasharray="8 5"
                  opacity="0.5"
                />
                <text x={gpiLine.chartW + 6} y={gpiLine.parityY + 4} fill="#0f766e" fontSize="11" fontWeight="900">
                  Parity
                </text>
                <g className="aag-axis-label" textAnchor="middle">
                  {gpiLine.xLabels.map((lbl) => (
                    <text key={`gxl-${lbl.age}`} x={lbl.x} y="300">
                      {lbl.age}
                    </text>
                  ))}
                </g>
              </g>
            </svg>
          </div>
        </article>

        <article className="aag-panel aag-table-card">
          <div className="aag-table-head">
            <div>
              <h3>Age-Wise Enrolment Table</h3>
              <p>Exact values with parity status for quick validation.</p>
            </div>
            <span className="aag-pill">Sample rows</span>
          </div>
          <table aria-label="Age-wise gender enrolment table">
            <thead>
              <tr>
                <th>Age</th>
                <th>Boys</th>
                <th>Girls</th>
                <th>Total</th>
                <th>GPI</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => (
                <tr key={row.age}>
                  <td>{row.age}</td>
                  <td className="num aag-boys-text">{fmt(row.boys)}</td>
                  <td className="num aag-girls-text">{fmt(row.girls)}</td>
                  <td className="num">{fmt(row.total)}</td>
                  <td>{row.gpi}</td>
                  <td>
                    <span className={`aag-tag ${row.status.tagClass}`.trim()}>{row.status.label}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>
      </section>

      <section className="aag-panel aag-journey-panel">
        <div className="aag-panel-head">
          <div>
            <h3>Enrolment Journey and Drop-Off Lens</h3>
            <p>
              A simplified journey card makes the retention story visible without forcing users to
              compare many tiny tiles.
            </p>
          </div>
          <span className="aag-pill">Retention view</span>
        </div>
        <div className="aag-journey-grid">
          {journey.map((card) => (
            <div key={card.label} className={`aag-journey-card ${card.className}`.trim()}>
              <i style={{ width: `${card.barWidth}%` }} />
              <small>{card.label}</small>
              <strong>{card.value}</strong>
              <span>{card.sub}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="aag-section-title" id="aag-actions">
        <div>
          <h2>Priority Actions for District Review</h2>
          <p>
            The redesigned tab ends with focused actions, so dashboard users can move from insight
            to follow-up quickly.
          </p>
        </div>
        <button type="button" className="aag-section-action" onClick={() => scrollToId("aag-hero")}>
          Back to top ↑
        </button>
      </div>

      <section className="aag-action-grid">
        {actions.map((action) => {
          const Icon = ACTION_ICONS[action.iconClass] || Users;
          return (
            <article key={action.title} className="aag-action-item">
              <div className={`aag-icon-box ${action.iconClass}`.trim()}>
                <Icon size={22} strokeWidth={2} />
              </div>
              <div>
                <h4>{action.title}</h4>
                <p>{action.body}</p>
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );
};

export default AgeEnrolmentAgeGenderTab;
